// The superadmin surface: how a customer is onboarded.
//
// Not part of any workspace, so it has its own door and none of the store. A
// superadmin is on no roster; they ask for a link here, and the server answers
// the same way whether or not the address is one.
//
// What it does: create a workspace (and its first member, in the same act), see
// every workspace and who is on each, add people to a roster, and hold the three
// settings a workspace may never widen for itself: the allow-list, guest access,
// and whether it is the resettable demo. What it deliberately does not do: read a
// workspace's records. See the header of worker/admin.ts.

import { useCallback, useEffect, useState } from 'react'

import { Button } from '../components/common'
import { Icon } from '../components/Icon'
import { Brand } from '../components/Mark'
import { adminApi, api, ApiError, type AdminMember, type AdminWorkspace, type Me } from '../lib/api'
import { goAs, hopTo, superadminHop } from '../lib/identities'

const field = {
  borderColor: 'color-mix(in srgb, var(--fg) 14%, transparent)',
  background: 'var(--panel)',
  color: 'var(--fg)',
}

const primary = { background: 'var(--accent)', color: 'var(--accent-contrast)' }

export function AdminPage() {
  const [me, setMe] = useState<Me | null>(null)
  const [state, setState] = useState<'loading' | 'out' | 'denied' | 'in'>('loading')

  useEffect(() => {
    api
      .me()
      .then((m) => {
        setMe(m)
        setState(m.superadmin ? 'in' : 'denied')
      })
      .catch((e) => setState(e instanceof ApiError && e.status === 401 ? 'out' : 'denied'))
  }, [])

  if (state === 'loading') return null
  if (state === 'out') return <Door />
  if (state === 'denied') {
    // The active identity is not a superadmin — but another one on this device may
    // be, and it was already proved. Mailing a fresh link to an address sitting two
    // inches away in the same cookie jar is the thing this screen used to do.
    const admin = superadminHop(me)
    return (
      <Frame>
        {admin ? (
          <>
            <p className="text-body leading-relaxed">
              {me?.me.email ? <b>{me.me.email}</b> : 'This account'} is not a superadmin, but{' '}
              <b>{admin.email}</b> is, and is signed in on this device.
            </p>
            <button
              type="button"
              onClick={() => void goAs(admin.token, '/admin')}
              className="rounded-control text-body mt-3 w-full px-3 py-2 font-semibold"
              style={primary}
            >
              Continue as {admin.email}
            </button>
            <p className="meta mt-3">
              Both stay signed in;{' '}
              <a href="/home" className="underline underline-offset-2">
                your workspaces
              </a>{' '}
              are still there.
            </p>
          </>
        ) : (
          <p className="text-body">
            {me?.me.email ? <b>{me.me.email}</b> : 'This account'} is not a superadmin.{' '}
            <a href="/home" className="underline underline-offset-2">
              Go to your workspaces
            </a>
            .
          </p>
        )}
      </Frame>
    )
  }
  return <Dashboard me={me!} />
}

function Frame({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="min-h-dvh px-6 py-10" style={{ background: 'var(--bg)', color: 'var(--fg)' }}>
      <div className={wide ? 'mx-auto w-full max-w-[64rem]' : 'mx-auto w-full max-w-[24rem]'}>
        <div className="mb-6 flex items-center gap-2.5">
          <Brand size={24} />
          <h1 className="text-title font-semibold tracking-tight">crum · workspaces</h1>
        </div>
        {children}
      </div>
    </div>
  )
}

function Door() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const expired = new URLSearchParams(location.search).get('expired') === '1'

  async function redeem(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api.submitCode(email.trim(), code.trim())
      location.assign('/admin')
    } catch {
      setError('That code did not work. Check the newest email, or ask for another.')
      setBusy(false)
    }
  }

  return (
    <Frame>
      {expired ? (
        <p className="note mb-3">That link has expired or has already been used.</p>
      ) : null}
      {sent ? (
        <form onSubmit={redeem} className="flex flex-col gap-2">
          <p className="text-body leading-relaxed">
            If <b>{email}</b> is a superadmin, an email is on its way. Tap the button in it, or type
            the code.
          </p>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            autoFocus
            className="num rounded-control text-title border px-3 py-2 text-center outline-none"
            style={{ ...field, letterSpacing: '0.3em' }}
          />
          <button
            type="submit"
            disabled={busy || code.length < 6}
            className="rounded-control text-body px-3 py-2 font-medium disabled:opacity-50"
            style={primary}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          {error ? (
            <p className="text-meta" style={{ color: 'var(--crit)' }}>
              {error}
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => {
              setSent(false)
              setCode('')
              setError('')
            }}
            className="meta self-start underline"
          >
            use a different address
          </button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void adminApi.requestLink(email.trim()).then(() => setSent(true))
          }}
          className="flex flex-col gap-2"
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@saltbark.com"
            autoFocus
            className="rounded-control text-body border px-3 py-2 outline-none"
            style={field}
          />
          <button
            type="submit"
            disabled={!email.includes('@')}
            className="rounded-control text-body px-3 py-2 font-medium disabled:opacity-50"
            style={primary}
          >
            Email me a way in
          </button>
        </form>
      )}
    </Frame>
  )
}

function Dashboard({ me }: { me: Me }) {
  const [list, setList] = useState<AdminWorkspace[]>([])
  const [open, setOpen] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    adminApi
      .workspaces()
      .then((r) => setList(r.workspaces))
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load.'))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return (
    <Frame wide>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <p className="meta">
          signed in as <b>{me.me.email}</b>
        </p>
        <span className="ml-auto flex items-center gap-2">
          <a href="/home" className="meta underline underline-offset-2">
            my workspaces
          </a>
          <button
            type="button"
            onClick={() => void api.logout().then(() => location.assign('/'))}
            className="meta underline underline-offset-2"
          >
            sign out
          </button>
        </span>
      </div>

      {error ? (
        <p className="text-body mb-3" style={{ color: 'var(--crit)' }}>
          {error}
        </p>
      ) : null}

      <section className="mb-8">
        <div className="mb-2 flex items-center gap-3">
          <h2 className="text-lead font-semibold">Workspaces</h2>
          <span className="meta">{list.length}</span>
          <button type="button" onClick={() => setCreating((c) => !c)} className="adder ml-auto">
            <Icon name="Plus" size={14} /> New workspace
          </button>
        </div>

        {creating ? (
          <CreateForm
            onDone={() => {
              setCreating(false)
              load()
            }}
          />
        ) : null}

        <div className="rows">
          {list.map((w) => (
            <div key={w.slug} className="py-2.5">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <button
                  type="button"
                  onClick={() => setOpen(open === w.slug ? null : w.slug)}
                  className="text-body text-left font-medium"
                >
                  {w.name}
                </button>
                <WorkspaceLink me={me} slug={w.slug} />
                <span className="meta">
                  {w.members} {w.members === 1 ? 'person' : 'people'}
                </span>
                {w.isDemo ? <Pill>demo</Pill> : null}
                {w.guestAccess ? <Pill>guest access</Pill> : null}
                {w.status === 'suspended' ? <Pill tone="crit">suspended</Pill> : null}
                <span className="meta num ml-auto" style={{ fontSize: 'var(--text-label)' }}>
                  {w.allowList.length ? w.allowList.join(', ') : 'no allow-list'}
                </span>
              </div>
              {open === w.slug ? <WorkspacePanel ws={w} onChange={load} /> : null}
            </div>
          ))}
          {list.length === 0 ? <p className="meta py-3">No workspaces yet.</p> : null}
        </div>
      </section>

      <Superadmins me={me} />
    </Frame>
  )
}

/**
 * The address of a workspace, opened as whoever on this device can open it.
 *
 * A superadmin is on no roster, so following this link as the superadmin identity
 * landed on that workspace's sign-in screen — which then mailed a link for an
 * address already proved here. Where a seat exists on this device, the hop is
 * silent except for the line under the address saying whose it is.
 */
function WorkspaceLink({ me, slug }: { me: Me; slug: string }) {
  const hop = hopTo(me, slug)
  const href = `/w/${slug}`
  if (!hop?.token) {
    return (
      <a href={href} className="num text-meta underline underline-offset-2">
        /w/{slug}
      </a>
    )
  }
  return (
    <button
      type="button"
      onClick={() => void goAs(hop.token, href)}
      className="num text-meta text-left underline underline-offset-2"
      title={`Open as ${hop.via}`}
    >
      /w/{slug} <span style={{ color: 'var(--faint)' }}>as {hop.via}</span>
    </button>
  )
}

function Pill({ children, tone }: { children: React.ReactNode; tone?: 'crit' }) {
  return (
    <span
      className="rounded-inline px-1.5 py-0.5"
      style={{
        fontFamily: 'var(--mono)',
        fontSize: 'var(--text-micro)',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: tone === 'crit' ? 'var(--crit)' : 'var(--muted)',
        border: '1px solid var(--border)',
      }}
    >
      {children}
    </span>
  )
}

function CreateForm({ onDone }: { onDone: () => void }) {
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const [allowList, setAllowList] = useState('')
  const [firstName, setFirstName] = useState('')
  const [firstEmail, setFirstEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await adminApi.createWorkspace({
        slug: slug.trim().toLowerCase(),
        name: name.trim(),
        allowList,
        first: { name: firstName, email: firstEmail },
      })
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create it.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-control mb-4 grid gap-2 border p-3 sm:grid-cols-2"
      style={{ borderColor: 'var(--border)' }}
    >
      <label className="flex flex-col gap-1">
        <span className="meta">Name</span>
        <input
          value={name}
          onChange={(e) => {
            setName(e.target.value)
            if (!slug) setSlug(slugify(e.target.value))
          }}
          placeholder="Knowledge Futures"
          className="rounded-control text-body border px-3 py-1.5 outline-none"
          style={field}
          autoFocus
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="note">Slug · the address is /w/&lt;slug&gt;</span>
        <input
          value={slug}
          onChange={(e) => setSlug(slugify(e.target.value))}
          placeholder="kf"
          className="num rounded-control text-body border px-3 py-1.5 outline-none"
          style={field}
        />
      </label>
      <label className="flex flex-col gap-1 sm:col-span-2">
        <span className="note">
          Allow-list · comma-separated emails and @domains that may ever hold a team seat
        </span>
        <input
          value={allowList}
          onChange={(e) => setAllowList(e.target.value)}
          placeholder="@knowledgefutures.org"
          className="num rounded-control text-body border px-3 py-1.5 outline-none"
          style={field}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="meta">First member · name</span>
        <input
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Travis Rich"
          className="rounded-control text-body border px-3 py-1.5 outline-none"
          style={field}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="note">First member · email (gets the first link, as admin)</span>
        <input
          type="email"
          value={firstEmail}
          onChange={(e) => setFirstEmail(e.target.value)}
          placeholder="travis@knowledgefutures.org"
          className="rounded-control text-body border px-3 py-1.5 outline-none"
          style={field}
        />
      </label>
      <div className="flex items-center gap-3 sm:col-span-2">
        <button
          type="submit"
          disabled={busy || !slug || !name.trim() || !firstEmail.includes('@')}
          className="rounded-control text-body px-3 py-1.5 font-medium disabled:opacity-50"
          style={primary}
        >
          {busy ? 'Creating…' : 'Create and send the first link'}
        </button>
        {error ? (
          <span className="text-body" style={{ color: 'var(--crit)' }}>
            {error}
          </span>
        ) : null}
      </div>
    </form>
  )
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

function WorkspacePanel({ ws, onChange }: { ws: AdminWorkspace; onChange: () => void }) {
  const [members, setMembers] = useState<AdminMember[] | null>(null)
  const [allowList, setAllowList] = useState(ws.allowList.join(', '))
  const [invName, setInvName] = useState('')
  const [invEmail, setInvEmail] = useState('')
  const [invAdmin, setInvAdmin] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [confirm, setConfirm] = useState('')
  const [msg, setMsg] = useState('')

  const loadMembers = useCallback(() => {
    adminApi
      .members(ws.slug)
      .then((r) => setMembers(r.members))
      .catch(() => setMembers([]))
  }, [ws.slug])

  useEffect(() => {
    loadMembers()
  }, [loadMembers])

  async function patch(p: Parameters<typeof adminApi.patchWorkspace>[1]) {
    setMsg('')
    try {
      await adminApi.patchWorkspace(ws.slug, p)
      onChange()
      setMsg('saved')
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'failed')
    }
  }

  async function invite(e: React.FormEvent) {
    e.preventDefault()
    setMsg('')
    try {
      await adminApi.invite(ws.slug, {
        name: invName,
        email: invEmail,
        access: invAdmin ? 'admin' : 'default',
      })
      setInvName('')
      setInvEmail('')
      setInvAdmin(false)
      loadMembers()
      onChange()
      setMsg('added, and a link is on its way')
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'failed')
    }
  }

  const internal = (members ?? []).filter((m) => m.kind === 'internal')
  const clients = (members ?? []).filter((m) => m.kind === 'client')

  return (
    <div
      className="rounded-control mt-3 grid gap-4 border p-3 lg:grid-cols-2"
      style={{ borderColor: 'var(--border)' }}
    >
      <div>
        <p className="eyebrow mb-1.5">Team seats</p>
        {members === null ? (
          <p className="meta">loading…</p>
        ) : internal.length === 0 ? (
          <p className="note">Nobody. This workspace cannot be entered.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {internal.map((m) => (
              <li key={m.email} className="text-body flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium">{m.name}</span>
                <span className="num" style={{ fontSize: 'var(--text-meta)' }}>
                  {m.email}
                </span>
                <span className="meta">
                  {m.access === 'admin' ? 'admin' : m.access === 'none' ? 'no access' : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
        {clients.length > 0 ? (
          <p className="meta mt-2">
            plus {clients.length} client {clients.length === 1 ? 'contact' : 'contacts'} with a page
          </p>
        ) : null}

        <form onSubmit={invite} className="mt-3 flex flex-col gap-1.5">
          <p className="eyebrow">Add a person to the team</p>
          <input
            value={invName}
            onChange={(e) => setInvName(e.target.value)}
            placeholder="Name"
            className="rounded-control text-body border px-2.5 py-1.5 outline-none"
            style={field}
          />
          <input
            type="email"
            value={invEmail}
            onChange={(e) => setInvEmail(e.target.value)}
            placeholder="email"
            className="rounded-control text-body border px-2.5 py-1.5 outline-none"
            style={field}
          />
          <label className="meta flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={invAdmin}
              onChange={(e) => setInvAdmin(e.target.checked)}
            />
            admin
          </label>
          <button
            type="submit"
            disabled={!invEmail.includes('@')}
            className="rounded-control text-body self-start px-3 py-1.5 font-medium disabled:opacity-50"
            style={primary}
          >
            Add and send a link
          </button>
        </form>
      </div>

      <div className="flex flex-col gap-3">
        <div>
          <p className="eyebrow mb-1.5">Allow-list</p>
          <div className="flex gap-2">
            <input
              value={allowList}
              onChange={(e) => setAllowList(e.target.value)}
              placeholder="@yourcompany.com, someone@elsewhere.com"
              className="num rounded-control text-body flex-1 border px-2.5 py-1.5 outline-none"
              style={field}
            />
            <button
              type="button"
              onClick={() => void patch({ allowList })}
              className="rounded-control text-body border px-3 py-1.5"
              style={{ borderColor: 'var(--border)' }}
            >
              Save
            </button>
          </div>
          <p className="note mt-1">
            Which addresses may ever hold a team seat here. The team's People list can only narrow
            this, never widen it. Empty means the roster alone decides.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-body flex items-center gap-2">
            <input
              type="checkbox"
              checked={ws.guestAccess}
              onChange={(e) => void patch({ guestAccess: e.target.checked })}
            />
            Guest access · sign in with a name and no credential. Demos only.
          </label>
          <label className="text-body flex items-center gap-2">
            <input
              type="checkbox"
              checked={ws.isDemo}
              onChange={(e) => void patch({ isDemo: e.target.checked })}
            />
            Demo · emptied and reseeded nightly. Nothing else can empty a log.
          </label>
          <label className="text-body flex items-center gap-2">
            <input
              type="checkbox"
              checked={ws.status === 'suspended'}
              onChange={(e) => void patch({ status: e.target.checked ? 'suspended' : 'active' })}
            />
            Suspended · refuses everyone until switched back
          </label>
        </div>

        {/* Emptying a log is the most destructive thing this surface can do, so it
            asks for the slug rather than a confirmation dialog: typing the name of
            the thing is the one gesture nobody performs by accident. */}
        <div className="flex flex-col gap-1.5">
          <p className="eyebrow">Danger</p>
          {resetting ? (
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                setMsg('')
                void adminApi
                  .reset(ws.slug, confirm)
                  .then((r) => {
                    setResetting(false)
                    setConfirm('')
                    loadMembers()
                    onChange()
                    setMsg(`emptied · ${r.restored} people kept on the roster`)
                  })
                  .catch((err) => setMsg(err instanceof Error ? err.message : 'failed'))
              }}
            >
              <input
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={ws.slug}
                autoFocus
                className="num rounded-control text-meta border px-2 py-1 outline-none"
                style={{ borderColor: 'var(--crit)', background: 'var(--bg)' }}
              />
              <button
                type="submit"
                disabled={confirm.trim() !== ws.slug}
                className="rounded-control text-meta px-2.5 py-1 font-semibold disabled:opacity-40"
                style={{ background: 'var(--crit)', color: 'var(--panel)' }}
              >
                Empty every record
              </button>
              <button
                type="button"
                onClick={() => {
                  setResetting(false)
                  setConfirm('')
                }}
                className="meta underline"
              >
                cancel
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setResetting(true)}
              className="rounded-control note self-start border px-2.5 py-1"
              style={{ borderColor: 'var(--crit)', color: 'var(--crit)' }}
            >
              Empty this workspace's records…
            </button>
          )}
          <p className="note leading-relaxed">
            Drops every event and every staged suggestion. The brand, the client links and the team
            survive: the roster is read first and re-seeded, so the same people can still sign in.
            There is no undo — take an export first.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() =>
              void adminApi
                .rebuild(ws.slug)
                .then((r) => {
                  loadMembers()
                  setMsg(`rebuilt, ${r.members} rows`)
                })
                .catch(() => setMsg('rebuild failed'))
            }
            className="meta underline underline-offset-2"
          >
            rebuild the roster projection
          </button>
          {msg ? <span className="meta">{msg}</span> : null}
        </div>
      </div>
    </div>
  )
}

function Superadmins({ me }: { me: Me }) {
  const [list, setList] = useState<Array<{ email: string; source: string }>>([])
  const [email, setEmail] = useState('')

  const load = useCallback(() => {
    adminApi
      .superadmins()
      .then((r) => setList(r.superadmins))
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return (
    <section>
      <h2 className="text-lead mb-2 font-semibold">Superadmins</h2>
      <div className="rows">
        {list.map((s) => (
          <div key={s.email} className="text-body flex flex-wrap items-baseline gap-x-3 py-2">
            <span className="num">{s.email}</span>
            <span className="meta">
              {s.source === 'env' ? 'bootstrap (env)' : `added by ${s.source}`}
            </span>
            {s.source !== 'env' && s.email !== me.me.email ? (
              <button
                type="button"
                onClick={() => void adminApi.removeSuperadmin(s.email).then(load)}
                className="meta ml-auto underline underline-offset-2"
              >
                remove
              </button>
            ) : null}
          </div>
        ))}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void adminApi.addSuperadmin(email.trim()).then(() => {
            setEmail('')
            load()
          })
        }}
        className="mt-2 flex gap-2"
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="add a superadmin by email"
          className="rounded-control text-body flex-1 border px-2.5 py-1.5 outline-none sm:max-w-[22rem]"
          style={field}
        />
        <Button type="submit" disabled={!email.includes('@')}>
          Add
        </Button>
      </form>
      <p className="note mt-2">
        A superadmin creates workspaces and manages who is on each roster. They do not read a
        workspace's records; to see one, they are added to its team like anybody else.
      </p>
    </section>
  )
}
